import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { reportApi } from '@/lib/api'
import { Trash2, FileText, Search, Loader2, ChevronLeft, ChevronRight, Plus } from 'lucide-react'

export function ReportListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [reports, setReports] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [reportType, setReportType] = useState(0)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const pageSize = 15

  const typeLabels: Record<number, string> = {
    1: 'dailyReport',
    2: 'weeklyReport',
    3: 'monthlyReport',
  }

  const statusBadges: Record<number, { variant: any; text: string }> = {
    0: { variant: 'secondary', text: 'generating' },
    1: { variant: 'warning', text: 'editing' },
    2: { variant: 'success', text: 'completed' },
    3: { variant: 'destructive', text: 'failed' },
  }

  const loadReports = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await reportApi.list({
        pageNum: page,
        reportType: reportType || undefined,
        nameSearch: searchName || undefined,
      })
      const list = res?.data || res || []
      setReports(Array.isArray(list) ? list : (list?.list || []))
      setTotal(res?.total || res?.count || 0)
    } catch (err) {
      console.error('Failed to load reports:', err)
    } finally {
      setLoading(false)
    }
  }, [page, reportType, searchName])

  useEffect(() => {
    loadReports()
  }, [loadReports])

  const handleSearch = () => {
    setPage(1)
    loadReports()
  }

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) return
    if (!confirm(t('monitor.deleteConfirm'))) return
    try {
      await reportApi.batchDelete(selectedIds.join(','))
      setSelectedIds([])
      loadReports()
    } catch (err) {
      console.error('Batch delete failed:', err)
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('report.title')}</h2>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder={t('report.reportName')}
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="w-64"
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
            />
            <select
              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={reportType}
              onChange={(e) => setReportType(Number(e.target.value))}
            >
              <option value={0}>{t('common.all')}</option>
              <option value={1}>{t('report.dailyReport')}</option>
              <option value={2}>{t('report.weeklyReport')}</option>
              <option value={3}>{t('report.monthlyReport')}</option>
            </select>
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              {t('common.search')}
            </Button>
            <Button variant="outline" className="ml-auto" onClick={handleBatchDelete} disabled={selectedIds.length === 0}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('report.deleteSelected')} ({selectedIds.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <input
                    type="checkbox"
                    checked={selectedIds.length > 0 && selectedIds.length === reports.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(reports.map(r => String(r.report_id)))
                      } else {
                        setSelectedIds([])
                      }
                    }}
                  />
                </TableHead>
                <TableHead>{t('report.reportName')}</TableHead>
                <TableHead>{t('report.reportType')}</TableHead>
                <TableHead>{t('report.reportPeriod')}</TableHead>
                <TableHead>{t('report.reportStatus')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => {
                  const badge = statusBadges[report.report_status] || statusBadges[0]
                  const id = String(report.report_id)
                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(id)}
                          onChange={() => toggleSelect(id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{report.report_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {t(`report.${typeLabels[report.report_type] || 'dailyReport'}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {report.start_time} ~ {report.end_time}
                      </TableCell>
                      <TableCell>
                        <Badge variant={badge.variant}>{t(`report.${badge.text}`)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm"
                          onClick={() => { window.location.href = `/report/${id}` }}>
                          <FileText className="mr-1 h-4 w-4" />
                          {t('report.viewDetail')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('common.total')} {total} {t('common.items')}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
              {t('common.prev')}
            </Button>
            <span className="text-sm">
              {t('common.page')} {page} {t('common.of')} {Math.ceil(total / pageSize)}
            </span>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)}
              onClick={() => setPage(page + 1)}>
              {t('common.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
